import { Chip, Stack, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import sheetTemplatesServices from "services/sheet-templates-services";
import useOpen from "utils/useOpen";
import TemplatesDialog from "./TemplatesDialog";

export default function AllTemplates({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });

  const [templates, setTemplates] = useState(null);
  const [lastFilter, setLastFilter] = useState({ type: 1 });
  const [refreshSignal, setRefreshSignal] = useState(null);

  async function handleAddTemplate(newElement) {
    newElement = { ...newElement };
    newElement.entityElements?.forEach((entityElement, index) => {
      if (typeof entityElement.id == "string")
        delete newElement.entityElements[index].id;
    });
    newElement.properties?.forEach((property, index) => {
      if (typeof property.id == "string")
        delete newElement.properties[index].id;
    });
    const [response, data] =
      await sheetTemplatesServices.createTemplate(newElement);
    if (response.ok) {
      const [response, data] =
        await sheetTemplatesServices.getTemplates(lastFilter);
      if (response.ok) {
        setTemplates(data);
      }

      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateTemplate(newElement) {
    newElement = { ...newElement };
    newElement.entityElements?.forEach((entityElement, index) => {
      if (typeof entityElement.id == "string")
        delete newElement.entityElements[index].id;
    });
    newElement.properties?.forEach((property, index) => {
      if (typeof property.id == "string")
        delete newElement.properties[index].id;
    });

    console.log("NEW ELEMENT: ", newElement);
    const [response, data] = await sheetTemplatesServices.updateTemplate(
      newElement.id,
      newElement,
    );
    if (response.ok) {
      const [response, data] =
        await sheetTemplatesServices.getTemplates(lastFilter);
      if (response.ok) {
        setTemplates(data);
      }
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteTemplate(id) {
    const [response, data] = await sheetTemplatesServices.removeTemplate(id);
    if (response.ok) {
      const [response, data] =
        await sheetTemplatesServices.getTemplates(lastFilter);
      if (response.ok) {
        setTemplates(data);
      }
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!templates?.length)
      return templates?.length === 0
        ? [
            {
              name: "No templates were found.",
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return templates.map((template) => {
      return {
        name: <MDTypography variant="inherit">{template.name}</MDTypography>,

        type: (
          <MDTypography variant="inherit">
            {template.type == 1 ? "Measurable" : "Actionable"}
          </MDTypography>
        ),

        referenceCode: (
          <MDTypography variant="inherit">
            {template.referenceCode || "—"}
          </MDTypography>
        ),

        account: (
          <MDTypography variant="inherit">
            {template.account?.name || label("all")}
          </MDTypography>
        ),

        entity: (
          <Stack
            direction="row"
            overflow={"hidden"}
            flexWrap={"wrap"}
            sx={{ gap: 0.5 }}
          >
            {template.entities?.length > 0
              ? template.entities?.map((ent) => (
                  <Chip size="small" variant="outlined" label={ent.name} />
                ))
              : "—"}
          </Stack>
        ),

        regions: (
          <Stack
            direction="row"
            overflow={"hidden"}
            flexWrap={"wrap"}
            sx={{ gap: 0.5 }}
          >
            {template.regions?.length > 0
              ? template.regions?.map((region) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={region.description}
                  />
                ))
              : "—"}
          </Stack>
        ),

        actions: (
          <MDBox
            display="flex"
            flexDirection="row"
            justifyContent="end"
            alignItems="center"
          >
            {permissions.update && (
              <Tooltip title={label("Edit")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    open.change("update", {
                      template,
                    });
                  }}
                >
                  <Icon>edit</Icon>
                </IconButton>
              </Tooltip>
            )}
            {permissions.delete && (
              <Tooltip title={label("Delete")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    open.change("alert", {
                      template,
                    });
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              </Tooltip>
            )}
          </MDBox>
        ),
      };
    });
  }, [templates, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("type-singular"),
        accessor: "type",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
      },
      {
        Header: label("entity-plural"),
        accessor: "entity",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteTemplate(open.get("alert").template.id);
          }}
        />
      ) : null}
      {open.get("create") ? (
        <TemplatesDialog
          type="create"
          isOpen={!!open.get("create")}
          onSubmit={handleAddTemplate}
          data={{ type: lastFilter.type }}
          onClose={() => open.change("create", false)}
        />
      ) : null}
      {open.get("update") ? (
        <TemplatesDialog
          type="edit"
          isOpen={!!open.get("update")}
          onSubmit={handleUpdateTemplate}
          data={{ template: open.get("update").template }}
          onClose={() => open.change("update", false)}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {"Templates"}{" "}
                  {"(0)" ||
                    (permissions.get ? `(${templates?.length || 0})` : "")}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", { type: lastFilter.type });
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;Create new{" "}
                    {lastFilter.type == 1 ? "measurable" : "actionable"}{" "}
                    template
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox
                    px={2}
                    mt={2}
                    display="flex"
                    flexDirection="row"
                    gap={1}
                  >
                    <Chip
                      label={"Measurable"}
                      onClick={() => {
                        setLastFilter((f) => ({ ...f, type: 1 }));
                        setRefreshSignal(Math.random());
                      }}
                      color={lastFilter.type == 1 ? "info" : undefined}
                      sx={
                        lastFilter.type == 1
                          ? { color: "white !important" }
                          : undefined
                      }
                      variant={lastFilter.type == 1 ? undefined : "outlined"}
                    />
                    <Chip
                      label={"Actionable"}
                      onClick={() => {
                        setLastFilter((f) => ({ ...f, type: 2 }));
                        setRefreshSignal(Math.random());
                      }}
                      color={lastFilter.type == 2 ? "info" : undefined}
                      sx={
                        lastFilter.type == 2
                          ? { color: "white !important" }
                          : undefined
                      }
                      variant={lastFilter.type == 2 ? undefined : "outlined"}
                    />
                  </MDBox>

                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-templates"
                      internalLoading={!Boolean(templates)}
                      fullWidth
                      setData={setTemplates}
                      refreshSignal={refreshSignal}
                      onSearch={async (request, callback) => {
                        setLastFilter((r) => ({ ...request, ...r }));
                        callback(
                          await sheetTemplatesServices.getTemplates({
                            ...request,
                            ...lastFilter,
                          }),
                        );
                      }}
                      label={"Search templates"}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(templates)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
